<template>
  <div :style="!$smallScreen ? 'margin-right: 200px' : '10px'">
    <div v-if="isInactiveLink" class="estimate-link-inactive-text">
      <h2>This link has been deactivated. Please contact the branch if you have any questions.</h2>
    </div>
    <div v-else-if="loading" class="text-center px-1">
      <b-spinner></b-spinner>
    </div>
    <div v-else>
      <b-overlay :show="submitting" :opacity="0.6">
        <div v-if="$smallScreen" style="padding-bottom: 70px"></div>
        <!-- Estimate Details Section -->

        <div class="mt-4 ml-2">
          <h3 class="col-head" style="font-size: 1.1rem">Estimate Details</h3>
          <hr style="border-color: gray; margin-top: -0.15rem" />

          <b-row no-gutters>
            <b-col cols="12" md="6">
              <div>
                <span class="col-head mr-3">Estimate ID:</span>
                <span class="col-form-label">{{ estimate.serviceOrderId }}</span>
              </div>
            </b-col>
            <b-col cols="12" md="6">
              <div>
                <span class="col-head mr-3">Pricing Valid Until:</span>
                <span class="col-form-label">{{ estimate.dateExpire | date }}</span>
              </div>
            </b-col>
          </b-row>
          <b-row no-gutters>
            <b-col cols="12" md="6">
              <div>
                <span class="col-head mr-3">Notified of Assessment:</span>
                <span class="col-form-label">{{ estimate.dateNotifyDiag | date }}</span>
              </div>
            </b-col>
            <b-col cols="12" md="6">
              <div>
                <span class="col-head mr-3">Authorized:</span>
                <span class="col-form-label">{{ estimate.dateAuthorize | dateTBD }}</span>
              </div>
            </b-col>
          </b-row>
        </div>

        <!-- Customer Information Section -->
        <div class="mt-4 ml-2">
          <h5 class="col-head" style="font-size: 1.1rem">Customer Details</h5>
          <hr style="border-color: gray; margin-top: -0.15rem" />

          <b-row no-gutters class="mt-2">
            <b-col cols="12" md="6">
              <div>
                <span class="col-head mr-3">Customer Name:</span>
                <span class="col-form-label">{{ customerName }}</span>
              </div>
            </b-col>
            <b-col cols="12" md="6">
              <div>
                <span class="col-head mr-3">Customer ID:</span>
                <span class="col-form-label">{{ customerId }}</span>
              </div>
            </b-col>
          </b-row>
        </div>
        <!-- Equipment Details Section -->
        <div class="mt-4 ml-2">
          <h5 class="col-head" style="font-size: 1.1rem">Equipment Details</h5>
          <hr style="border-color: gray; margin-top: -0.15rem" />

          <b-row no-gutters class="mt-2">
            <b-col cols="12" md="6">
              <div>
                <span class="col-head mr-3">Fleet Unit ID:</span>
                <span class="col-form-label">{{ estimate.fleetUnitId }}</span>
              </div>
            </b-col>
            <b-col cols="12" md="6">
              <div>
                <span class="col-head mr-3">VIN/Serial:</span>
                <span class="col-form-label">{{ estimate.vin }}</span>
              </div>
            </b-col>
          </b-row>
          <b-row no-gutters>
            <b-col cols="12" md="6">
              <div>
                <span class="col-head mr-3">Location:</span>
                <span class="col-form-label">{{ estimate.branchId }} - {{ estimate.branchName }}</span>
              </div>
            </b-col>
          </b-row>
        </div>

        <!-- Job Details Section -->
        <div class="mt-4 ml-2">
          <h5 class="col-head" style="font-size: 1.1rem">Job Details</h5>
          <hr style="border-color: gray; margin-top: -0.15rem" />
          <div v-for="job in estimate.jobs" :key="job.jobId" class="mt-3 mr-6">
            <estimate-link-line-item
              class="estimate-link-line-item"
              :job="job"
              :submitted="isSubmitted"
            ></estimate-link-line-item>
          </div>
        </div>
        <div v-if="isPORequired && !isSubmitted" class="estimate-link-instructions mt-2 mb-1">
          Please enter a Purchase Order number below. PO required to begin work
        </div>

        <div class="estimate-link-po-number ml-2">
          <b-form-group
            class="d-flex ml-4"
            label="PO #"
            label-class="col-form-label subheader"
            label-for="purchase-order"
          >
            <b-input-group class="ml-4">
              <b-form-input id="purchase-order" v-model="purchaseOrder" maxlength="30" :disabled="isSubmitted" />
              <span v-if="isPORequired" class="required-asterisk">*</span>
            </b-input-group>
          </b-form-group>
        </div>
        <div class="mt-3">
          <b-col :cols="$smallScreen ? '12' : '4'">
            <b-form-group
              :label-cols="labelCols"
              label="Subtotal:"
              label-for="subtotal"
              class="m-0"
              label-class="font-weight-bold py-1"
            >
              <div id="subtotal" class="col-form-label text-right py-1">
                <b>{{ estimate.subtotal || 0 | currency }}</b>
              </div>
            </b-form-group>
            <b-form-group
              :label-cols="labelCols"
              label="Shop Supplies:"
              label-for="shopSupplies"
              class="m-0"
              label-class="py-1"
            >
              <div id="shopSupplies" class="col-form-label text-right py-1">
                {{ estimate.shopSupplies || 0 | currency }}
              </div>
            </b-form-group>
            <b-form-group
              :label-cols="labelCols"
              label="EPA Charge:"
              label-for="epaCharge"
              class="m-0"
              label-class="py-1"
            >
              <div id="epaCharge" class="col-form-label text-right py-1">
                {{ estimate.epaCharge || 0 | currency }}
              </div>
            </b-form-group>
            <b-form-group
              :label-cols="labelCols"
              label="Pre-Tax Total:"
              label-for="preTaxTotal"
              class="m-0"
              label-class="font-weight-bold"
            >
              <div id="preTaxTotal" class="col-form-label text-right">
                <b>{{ preTaxTotal || 0 | currency }}</b>
              </div>
            </b-form-group>
            <b-form-group
              :label-cols="labelCols"
              label="Sales Tax:"
              label-for="salesTax"
              class="m-0"
              label-class="py-1"
            >
              <div id="salesTax" class="col-form-label text-right py-1">
                {{ estimate.salesTax || 0 | currency }}
              </div>
            </b-form-group>
            <b-form-group
              :label-cols="labelCols"
              label="Total:"
              label-for="total"
              class="m-0 pt-2"
              label-class="font-weight-bold"
            >
              <div id="total" class="col-form-label text-right">
                <b>{{ estimate.total || 0 | currency }}</b>
              </div>
            </b-form-group>
          </b-col>
          <div
            v-if="!isSubmitted"
            class="ml-2"
            :style="$smallScreen ? 'padding-bottom: 110px' : 'padding-bottom: 80px'"
          >
            <div v-if="hasAnyJobEstimateStatus || (!hasPONumber && isPORequired)" class="mt-4">
              <div v-if="hasAnyJobEstimateStatus" class="note ml-2 mb-2">
                All jobs must be Approved, Rejected, or Deferred to Submit Estimate
              </div>
              <div v-if="!hasPONumber && isPORequired" class="note">Purchase Order number must be entered</div>
            </div>
            <div>
              <b-button variant="primary" :disabled="!allowSubmit" @click="submitEstimate()">
                <div>SUBMIT ESTIMATE</div>
              </b-button>
            </div>
          </div>
        </div>
      </b-overlay>
    </div>
  </div>
</template>
<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import EstimateLinkLineItem from '@/vcp/pages/estimate-link/EstimateLinkLineItem.vue';
import { GuidLinkActions, GuidLinkGetters, GuidLinkMutations } from '@/shared/store/guid-link/types';
import ErrorService from '@/shared/services/ErrorService';
import SuccessService from '@/shared/services/SuccessService';

export default {
  name: 'EstimateLink',
  components: {
    'estimate-link-line-item': EstimateLinkLineItem
  },
  data() {
    return {
      loading: false,
      submitting: false,
      isInactiveLink: false,
      labelColsXl: '4',
      labelColsLg: '12',
      labelColsMd: '12',
      labelColsSm: '12',
      labelCols: '6'
    };
  },
  computed: {
    ...mapGetters({ estimate: GuidLinkGetters.GET_ESTIMATE_LINK }),
    estimateId() {
      return this.estimate.estimateId;
    },
    customerName() {
      return this.estimate.customer?.customerName;
    },
    customerId() {
      return this.estimate.customer?.customerId;
    },
    preTaxTotal() {
      return this.estimate.subtotal + this.estimate.epaCharge + this.estimate.shopSupplies;
    },
    hasAnyJobEstimateStatus() {
      return this.estimate.jobs?.some(job => job.jobOperationStatusId == 1);
    },
    isPORequired() {
      return this.estimate.customer?.poRequired;
    },
    hasPONumber() {
      return this.estimate.purchaseOrder?.length > 0;
    },
    allowSubmit() {
      if (this.isPORequired) {
        return !this.hasAnyJobEstimateStatus && this.hasPONumber;
      } else {
        return !this.hasAnyJobEstimateStatus;
      }
    },
    isSubmitted() {
      return this.estimate.guidLinkMaps[0]?.submitted;
    },
    purchaseOrder: {
      get() {
        return this.estimate.purchaseOrder;
      },
      set(value) {
        this[GuidLinkMutations.SET_ESTIMATE_LINK_PROP]({ key: 'purchaseOrder', value: value });
      }
    }
  },
  async created() {
    this.loading = true;
    try {
      await this[GuidLinkActions.FETCH_ESTIMATE_LINK_LOOKUPS]();
      await this[GuidLinkActions.FETCH_ESTIMATE_LINK](this.$route.params.guid);
    } catch (error) {
      let errorMessage = 'Error fetching Estimate.';
      if (error.response && error.response.status == 415) {
        errorMessage = 'Estimate Link has been deactivated.';
        this.isInactiveLink = true;
      }
      ErrorService.createErrorToast(this, errorMessage);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    ...mapActions([
      GuidLinkActions.FETCH_ESTIMATE_LINK,
      GuidLinkActions.FETCH_ESTIMATE_LINK_LOOKUPS,
      GuidLinkActions.SUBMIT_ESTIMATE_LINK
    ]),
    ...mapMutations([GuidLinkMutations.SET_ESTIMATE_LINK_PROP]),
    async submitEstimate() {
      try {
        this.submitting = true;
        this[GuidLinkMutations.SET_ESTIMATE_LINK_PROP]({
          key: 'submittedFromGuid',
          value: this.$route.params.guid
        });

        await this[GuidLinkActions.SUBMIT_ESTIMATE_LINK](this.estimate);
        SuccessService.createSuccessToast(
          this.$root,
          `Estimate submitted successfully. For questions or changes, please contact the branch`
        );
      } catch (error) {
        let errorMessage = 'Error submitting Estimate.';
        if (error.response && error.response.status == 415) {
          errorMessage = 'No changes were saved. Estimate Link has been deactivated.';
          this.isInactiveLink = true;
        }
        ErrorService.createErrorToast(this, errorMessage);
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>
